import { useEffect, useRef, useState } from "react";
import { useFetcher } from "react-router";
import type { Route } from "./+types/contact";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { GoldMesh } from "~/components/marketing/svg/GoldMesh";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";
import { MapPin, ArrowRight } from "lucide-react";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  return { locale };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale].contact.meta;
  return [
    { title: t.title },
    { name: "description", content: t.description },
    ...socialMeta({ title: t.title, description: t.description, pathname: "/contact", locale }),
  ];
}

function useReveal(threshold = 0.08) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add("visible"); },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

type TabKey = "brand" | "designer" | "general";

export default function ContactPage() {
  const { t } = useT();
  const c = t.contact;
  const fetcher = useFetcher<{ ok: boolean; error?: string }>();
  const heroRef = useReveal(0.15);
  const formRef = useReveal();
  const officesRef = useReveal();

  const [activeTab, setActiveTab] = useState<TabKey>("brand");

  const isSubmitting = fetcher.state !== "idle";
  const isSuccess = fetcher.data?.ok === true && !fetcher.data?.error;
  const errorKey = fetcher.data?.ok === false ? fetcher.data.error : null;
  const errorMessage =
    errorKey === "required_fields"
      ? c.errors.required
      : errorKey === "invalid_email"
        ? c.errors.invalidEmail
        : errorKey === "send_failed"
          ? c.errors.sendFailed
          : null;

  const TABS: { key: TabKey; label: string }[] = [
    { key: "brand", label: c.tabs.brand },
    { key: "designer", label: c.tabs.designer },
    { key: "general", label: c.tabs.general },
  ];

  return (
    <MarketingLayout>
      {/* ══ HERO ══ */}
      <section className="px-6 lg:px-12 pt-16 pb-14 lg:pt-24 lg:pb-20 relative overflow-hidden">
        <GoldMesh opacity={0.06} />
        <div ref={heroRef} className="reveal relative max-w-3xl">
          <p className="issue-label mb-6" style={{ fontFamily: "var(--font-sans)" }}>{c.overline}</p>
          <h1
            className="font-display font-light leading-[0.92] tracking-tight text-foreground"
            style={{ fontSize: "clamp(2.8rem, 6vw, 5.5rem)" }}
          >
            {c.heading}
          </h1>
          <p
            className="mt-8 text-muted-foreground leading-relaxed"
            style={{ fontSize: "clamp(0.92rem, 1.2vw, 1.05rem)", maxWidth: "56ch" }}
          >
            {c.subtitle}
          </p>
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ FORM SECTION ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div ref={formRef} className="reveal max-w-2xl">
          {isSuccess ? (
            <SuccessState c={c} />
          ) : (
            <>
              {/* ── Tabs ── */}
              <div className="flex flex-wrap gap-2 mb-12">
                {TABS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveTab(key)}
                    className={`h-9 px-5 rounded-full text-xs font-medium transition-all border ${
                      activeTab === key
                        ? "bg-teal text-teal-fg border-transparent"
                        : "bg-muted text-muted-foreground border-border hover:border-foreground/20"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {errorMessage && (
                <p className="text-sm text-destructive mb-6">{errorMessage}</p>
              )}

              {activeTab === "brand" && <BrandForm c={c} isSubmitting={isSubmitting} fetcher={fetcher} />}
              {activeTab === "designer" && <DesignerForm c={c} isSubmitting={isSubmitting} fetcher={fetcher} />}
              {activeTab === "general" && <GeneralForm c={c} isSubmitting={isSubmitting} fetcher={fetcher} />}
            </>
          )}
        </div>
      </section>

      <div className="px-6 lg:px-12"><div className="rule" /></div>

      {/* ══ OFFICES ══ */}
      <section className="px-6 lg:px-12 py-16 lg:py-24">
        <div ref={officesRef} className="reveal">
          <p className="issue-label mb-8" style={{ fontFamily: "var(--font-sans)" }}>{c.offices.heading}</p>
          <div className="grid sm:grid-cols-3 gap-8">
            {([c.offices.hongkong, c.offices.guangdong, c.offices.london] as const).map((office) => (
              <div key={office.city} className="group">
                <div className="flex items-center gap-2.5 mb-3">
                  <MapPin className="text-muted-foreground/40" style={{ width: 14, height: 14 }} />
                  <span
                    className="font-display font-light text-foreground"
                    style={{ fontSize: "clamp(1.1rem, 1.8vw, 1.35rem)", letterSpacing: "0.02em" }}
                  >
                    {office.city}
                  </span>
                </div>
                <p
                  className="text-muted-foreground"
                  style={{ fontSize: "0.82rem", lineHeight: 1.6, paddingLeft: "1.6rem" }}
                >
                  {office.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

// ─── Success State ──────────────────────────────────────────────────────────

function SuccessState({ c }: { c: ReturnType<typeof useT>["t"]["contact"] }) {
  return (
    <div>
      <h2
        className="font-display font-light text-foreground leading-tight mb-4"
        style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)" }}
      >
        {c.success.heading}
      </h2>
      <p
        className="text-muted-foreground leading-relaxed mb-10"
        style={{ fontSize: "0.92rem", maxWidth: "52ch" }}
      >
        {c.success.body}
      </p>
      <div className="flex flex-wrap gap-4">
        <a
          href="/"
          className="inline-block px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors"
          style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
        >
          {c.success.backToHome}
        </a>
        <a
          href="/sign-in"
          className="inline-flex items-center gap-2 px-8 py-4 border border-border hover:border-foreground/30 transition-colors"
          style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
        >
          {c.success.explorePlatform}
          <ArrowRight style={{ width: 12, height: 12 }} />
        </a>
      </div>
    </div>
  );
}

// ─── Brand Form ─────────────────────────────────────────────────────────────

function BrandForm({
  c,
  isSubmitting,
  fetcher,
}: {
  c: ReturnType<typeof useT>["t"]["contact"];
  isSubmitting: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetcher: any;
}) {
  const f = c.brand;
  return (
    <div>
      <h2
        className="font-display font-light text-foreground leading-tight mb-3"
        style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
      >
        {f.headline}
      </h2>
      <p className="text-muted-foreground leading-relaxed mb-10" style={{ fontSize: "0.88rem", maxWidth: "56ch" }}>
        {f.copy}
      </p>

      <fetcher.Form method="post" action="/resources/contact-inquiry" className="space-y-6">
        <input type="hidden" name="formType" value="brand" />
        <input type="text" name="website_url" className="sr-only" tabIndex={-1} autoComplete="off" aria-hidden="true" />

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={`${f.firstName} *`} name="firstName" required />
          <Field label={`${f.lastName} *`} name="lastName" required />
        </div>
        <Field label={`${f.workEmail} *`} name="workEmail" type="email" required />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={`${f.companyName} *`} name="companyName" required />
          <Field label={f.companyWebsite} name="companyWebsite" type="url" placeholder="https://" />
        </div>
        <SelectField
          label={`${f.helpWith} *`}
          name="helpWith"
          options={f.helpOptions as unknown as string[]}
          placeholder={f.selectPlaceholder}
          required
        />
        <TextArea
          label={f.message}
          name="message"
          placeholder={f.messagePlaceholder}
        />
        <SubmitButton label={isSubmitting ? f.submitting : f.submit} disabled={isSubmitting} />
      </fetcher.Form>
    </div>
  );
}

// ─── Designer Form ──────────────────────────────────────────────────────────

function DesignerForm({
  c,
  isSubmitting,
  fetcher,
}: {
  c: ReturnType<typeof useT>["t"]["contact"];
  isSubmitting: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetcher: any;
}) {
  const f = c.designer;
  return (
    <div>
      <h2
        className="font-display font-light text-foreground leading-tight mb-3"
        style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
      >
        {f.headline}
      </h2>
      <p className="text-muted-foreground leading-relaxed mb-10" style={{ fontSize: "0.88rem", maxWidth: "56ch" }}>
        {f.copy}
      </p>

      <fetcher.Form method="post" action="/resources/contact-inquiry" className="space-y-6">
        <input type="hidden" name="formType" value="designer" />
        <input type="text" name="website_url" className="sr-only" tabIndex={-1} autoComplete="off" aria-hidden="true" />

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={`${f.firstName} *`} name="firstName" required />
          <Field label={`${f.lastName} *`} name="lastName" required />
        </div>
        <Field label={`${f.email} *`} name="email" type="email" required />
        <Field label={f.portfolioUrl} name="portfolioUrl" type="url" placeholder="https://" />
        <div className="grid sm:grid-cols-2 gap-4">
          <SelectField
            label={f.cadSoftware}
            name="cadSoftware"
            options={f.cadOptions as unknown as string[]}
            placeholder={f.selectPlaceholder}
          />
          <SelectField
            label={f.designCount}
            name="designCount"
            options={f.designCountOptions as unknown as string[]}
            placeholder={f.designCountPlaceholder}
          />
        </div>
        <TextArea
          label={f.message}
          name="message"
          placeholder={f.messagePlaceholder}
        />
        <SubmitButton label={isSubmitting ? f.submitting : f.submit} disabled={isSubmitting} />
      </fetcher.Form>
    </div>
  );
}

// ─── General Form ───────────────────────────────────────────────────────────

function GeneralForm({
  c,
  isSubmitting,
  fetcher,
}: {
  c: ReturnType<typeof useT>["t"]["contact"];
  isSubmitting: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fetcher: any;
}) {
  const f = c.general;
  return (
    <div>
      <h2
        className="font-display font-light text-foreground leading-tight mb-3"
        style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)" }}
      >
        {f.headline}
      </h2>
      <p className="text-muted-foreground leading-relaxed mb-10" style={{ fontSize: "0.88rem", maxWidth: "56ch" }}>
        {f.copy}
      </p>

      <fetcher.Form method="post" action="/resources/contact-inquiry" className="space-y-6">
        <input type="hidden" name="formType" value="general" />
        <input type="text" name="website_url" className="sr-only" tabIndex={-1} autoComplete="off" aria-hidden="true" />

        <Field label={`${f.name} *`} name="name" required />
        <Field label={`${f.email} *`} name="email" type="email" required />
        <SelectField
          label={`${f.topic} *`}
          name="topic"
          options={f.topicOptions as unknown as string[]}
          placeholder={f.selectPlaceholder}
          required
        />
        <TextArea
          label={f.message}
          name="message"
          placeholder={f.messagePlaceholder}
        />
        <SubmitButton label={isSubmitting ? f.submitting : f.submit} disabled={isSubmitting} />
      </fetcher.Form>
    </div>
  );
}

// ─── Shared Sub-components ──────────────────────────────────────────────────

function Field({
  label, name, type = "text", placeholder, required,
}: {
  label: string; name: string; type?: string; placeholder?: string; required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-2">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        className="w-full h-10 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:border-foreground/40 transition-colors text-foreground placeholder:text-muted-foreground/60"
      />
    </div>
  );
}

function SelectField({
  label, name, options, placeholder, required,
}: {
  label: string; name: string; options: string[]; placeholder: string; required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-2">
        {label}
      </label>
      <select
        id={name}
        name={name}
        required={required}
        defaultValue=""
        className="w-full h-10 px-3 text-sm bg-background border border-border rounded-lg focus:outline-none focus:border-foreground/40 transition-colors text-foreground appearance-none"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 12px center" }}
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function TextArea({
  label, name, placeholder,
}: {
  label: string; name: string; placeholder?: string;
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-[10px] tracking-[0.14em] uppercase text-muted-foreground mb-2">
        {label}
      </label>
      <textarea
        id={name}
        name={name}
        rows={4}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:border-foreground/40 transition-colors text-foreground placeholder:text-muted-foreground/60 resize-y"
      />
    </div>
  );
}

function SubmitButton({ label, disabled }: { label: string; disabled: boolean }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className="px-8 py-4 bg-teal text-teal-fg hover:bg-teal-hover transition-colors disabled:opacity-50"
      style={{ fontSize: "0.68rem", letterSpacing: "0.2em", textTransform: "uppercase" }}
    >
      {label}
    </button>
  );
}
